import { useState } from "react";
import { Card, Button, Progress, Typography, Space, message as antdMessage } from "antd";
import { DownloadOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { Trans } from "react-i18next";
import { apiUrl } from "../../api";
import { getFontClass } from "../../util";

const { Title, Text, Paragraph } = Typography;

export default function ReportDownloadPage() {
  const [progress, setProgress] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState("");

  const token = new URLSearchParams(window.location.search).get("token");

  const handleDownload = async () => {
    if (!token) {
      antdMessage.error("Invalid or missing download token.");
      setMessage("Invalid or missing download token.");
      return;
    }

    setDownloading(true);
    setMessage("Preparing your report...");

    try {
      const response = await fetch(`${apiUrl}/merchants/download?token=${token}`, {
        method: "GET",
      });

      if (!response.ok){
        if(response.status==401){
          throw new Error("Token expired");
        }else{
          throw new Error("Download failed");
        }
      } 

      // Get headers
      const contentLength = response.headers.get("Content-Length");
      const contentType = response.headers.get("Content-Type") || "application/octet-stream";
      const contentDisposition = response.headers.get("Content-Disposition");
      
      console.log("📥 Content-Type:", contentType);
      console.log("📥 Content-Disposition:", contentDisposition);
      
      // Determine file extension from Content-Type FIRST (most reliable)
      let extension = ".bin";
      if (contentType.includes("text/csv")) {
        extension = ".csv";
      } else if (contentType.includes("spreadsheetml") || contentType.includes("xlsx")) {
        extension = ".xlsx";
      } else if (contentType.includes("ms-excel") && !contentType.includes("xlsx")) {
        extension = ".xls";
      } else if (contentType.includes("pdf")) {
        extension = ".pdf";
      } else if (contentType.includes("zip")) {
        extension = ".zip";
      }
      
      console.log("📦 Detected extension from Content-Type:", extension);
      
      // Try to extract filename from Content-Disposition header
      let filename = "";
      if (contentDisposition) {
        // Try to extract filename (handles various formats)
        const match = contentDisposition.match(/filename\s*=\s*"?([^";\n]+)"?/i);
        if (match && match[1]) {
          filename = match[1].trim().replace(/^["']|["']$/g, '');
          console.log("✅ Extracted filename from header:", filename);
          
          // If extracted filename doesn't have correct extension, fix it
          if (!filename.endsWith(extension)) {
            // Remove any existing extension and add the correct one
            filename = filename.replace(/\.[^.]+$/, '') + extension;
            console.log("🔧 Fixed filename extension:", filename);
          }
        }
      }
      
      // If no filename extracted, use default with correct extension
      if (!filename) {
        filename = `transaction_report${extension}`;
        console.log("⚠️ Using default filename:", filename);
      }
      
      console.log("📄 Final filename:", filename);
      
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      let loaded = 0;

      if (response.body) {
        const reader = response.body.getReader();
        const chunks = [];

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          loaded += value.length;
          if (total) setProgress(Math.floor((loaded / total) * 100));
        }

        // Create blob with the EXACT content type from server
        const blob = new Blob(chunks, { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename; // Use the extracted filename
        document.body.appendChild(a); // Append to body for better compatibility
        a.click();
        document.body.removeChild(a); // Clean up
        
        // Delay cleanup to ensure download starts
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 100);

        setMessage("✅ Download complete");
        antdMessage.success("Report downloaded successfully!");
      }
    } catch (err) {
      console.error("Download error:", err);
      setMessage("❌ Error downloading file");
      antdMessage.error("Error downloading file");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "linear-gradient(135deg, #f4f6ff 0%, #e6e8f0 100%)",
        padding: 24,
      }}
    >
      <Card
        bordered={false}
        style={{
          width: "100%",
          maxWidth: 480,
          boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
          borderRadius: 16,
        }}
      >

     
        <div style={{ textAlign: "center", marginBottom: 24 }}>
        <Typography className={getFontClass("text-transparent bg-clip-text bg-gradient-to-r from-neo-primary to-neo-primary mt-2  mb-6 text-3xl",700)}>
                  NeoPaas
                </Typography>
          <SafetyCertificateOutlined
            style={{
              fontSize: 48,
              color: "#441752",
              backgroundColor: "#f9f0ff",
              padding: 12,
              borderRadius: "50%",
              marginBottom: 16,
            }}
          />
          <Title level={3} style={{ marginBottom: 8, color: "#1f1f1f" }}>
             Transaction Report
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Your report is ready for download.
          </Paragraph>
        </div>

        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            size="large"
            block
            loading={downloading}
            onClick={handleDownload}
            style={{
              borderRadius: 10,
              background: "linear-gradient(90deg, #722ed1 0%, #391085 100%)",
            }}
          >
            {downloading ? "Downloading..." : "Download Report"}
          </Button>

          {downloading && (
            <Progress
              percent={progress}
              status="active"
              strokeColor={{
                from: "#722ed1",
                to: "#391085",
              }}
            />
          )}

          {message && (
            <Text
              type={
                message.includes("Error")
                  ? "danger"
                  : message.includes("complete")
                  ? "success"
                  : "secondary"
              }
            >
              {message} {progress > 0 && progress < 100 && `${progress}%`}
            </Text>
          )}
        </Space>

        <Paragraph
          type="secondary"
          style={{
            textAlign: "center",
            marginTop: 32,
            fontSize: 12,
          }}
        >
          <Trans i18nKey="html.securedDownload">
            🔒 This download is secured by <strong>NeoPaas</strong>
          </Trans>
        </Paragraph>
      </Card>
    </div>
  );
}
